import React from "react";
import { Check, GripVertical } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { SubTask } from "../../types";

const SUBTASK_PRIORITY_COLORS: Record<number, string> = {
  1: "#b91c1c",
  2: "#f97316",
  3: "#facc15",
};

interface SortableBoardSubtaskRowProps {
  subtask: SubTask;
  isLast: boolean;
  onToggleSubTask: (subTaskId: string) => void;
  onSubtaskClick: (subtaskId: string) => void;
}

const SortableBoardSubtaskRow: React.FC<SortableBoardSubtaskRowProps> = ({
  subtask,
  isLast,
  onToggleSubTask,
  onSubtaskClick,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: subtask.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group/subtask flex items-center gap-2 px-3 py-2 hover:bg-warm-border-subtle transition-colors duration-150 ease-in-out ${
        !isLast ? "border-b border-warm-border-subtle" : ""
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        aria-label="Drag to reorder subtask"
        className="flex-shrink-0 text-warm-text-tertiary cursor-grab active:cursor-grabbing opacity-0 group-hover/subtask:opacity-100 transition-opacity duration-150 ease-in-out"
      >
        <GripVertical size={12} />
      </button>
      <button
        onClick={() => onToggleSubTask(subtask.id)}
        aria-label={
          subtask.completed
            ? `Mark subtask "${subtask.title}" as not done`
            : `Mark subtask "${subtask.title}" as done`
        }
        className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors duration-150 ease-in-out ${
          subtask.completed
            ? "bg-warm-blue border-warm-blue"
            : "border-warm-text-tertiary hover:border-warm-blue"
        }`}
      >
        {subtask.completed && <Check size={10} className="text-white" />}
      </button>
      {subtask.priority < 4 && (
        <span
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{
            backgroundColor: SUBTASK_PRIORITY_COLORS[subtask.priority],
          }}
        />
      )}
      <span
        onClick={(e) => {
          e.stopPropagation();
          onSubtaskClick(subtask.id);
        }}
        className={`text-xs flex-1 cursor-pointer hover:text-warm-blue transition-colors duration-150 ease-in-out ${
          subtask.completed
            ? "line-through text-warm-text-tertiary"
            : "text-warm-text-primary"
        }`}
      >
        {subtask.title}
      </span>
      {subtask.scheduled_date && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-warm-bg-base text-warm-text-tertiary flex-shrink-0">
          {subtask.scheduled_date}
        </span>
      )}
    </div>
  );
};

export default SortableBoardSubtaskRow;
